/**
 * "Where did my survey go?" — answered by the app, in one line (D-020).
 *
 * ── The hour this is worth ──────────────────────────────────────────────────
 * On 2026-08-06 a survey was visible in Safari and absent from the same phone's
 * installed app. Nothing had been deleted. iOS gives a Home Screen web app its
 * own storage container (D-019), and the two were simply two different piles of
 * data wearing the same icon and the same URL. Finding that out took an hour of
 * reading source. It should have taken five seconds of reading the screen.
 *
 * So the app says its own counts out loud: how many surveys are on THIS device,
 * how many still owe the office something, and which side of the wall this is.
 * Two phones side by side, or one phone twice, and the difference is legible to
 * a surveyor and to the office over the phone.
 *
 * ── And the truth about backups ─────────────────────────────────────────────
 * The survey list used to carry a standing saves-locally reassurance — true
 * and dangerously incomplete: an UNLINKED device has no backup at all, and
 * clearing site data takes the lot. `homeStorageNotice` is that sentence
 * rewritten to be honest without being an alarm — this is important information,
 * not an error, so it stays in the Notice's calm grey and never turns red
 * (D-011, D-015).
 *
 * Pure, and tested in the node suite: the copy is the part worth testing.
 */

import type { StorageSnapshot } from '../../db/store';

export interface StorageDiagnosticInput extends StorageSnapshot {
  /** From `isStandalone()` — the installed app, or a browser tab. */
  standalone: boolean;
  linked: boolean;
}

export interface StorageDiagnostic {
  /** The counts. One line, plain, readable aloud over a phone. */
  counts: string;
  /** Which container this is, in words a surveyor uses. */
  where: string;
  /** How many pictures this device is holding, and how much room they take (round 8.1). */
  pictures: string;
}

function plural(count: number, word: string): string {
  return `${String(count)} ${word}${count === 1 ? '' : 's'}`;
}

const MB = 1024 * 1024;

/**
 * The pictures line (round 8.1, task 1). Always one decimal on the megabyte
 * figure — "34.6 MB", never "35 MB" — because a surveyor deciding whether
 * there is room for a backup is reading this off a small screen and a rounded
 * whole number reads as more precise than it is.
 */
function picturesLine(photos: number, photoBytes: number): string {
  if (photos === 0) return 'No pictures on this phone.';
  return `${plural(photos, 'picture')} on this phone · ${(photoBytes / MB).toFixed(1)} MB`;
}

/**
 * The diagnostic line.
 *
 * `waiting` counts SURVEYS with something still owed, not outbox rows — see
 * `SurveyStore.storageSnapshot`. A surveyor asked "how many are waiting to go
 * in", and eleven queued photos on one job is one job.
 */
export function storageDiagnostic(input: StorageDiagnosticInput): StorageDiagnostic {
  const counts =
    input.surveys === 0
      ? 'No surveys on this device.'
      : input.waiting === 0
        ? `${plural(input.surveys, 'survey')} on this device — nothing waiting to send.`
        : `${plural(input.surveys, 'survey')} on this device — ` +
          `${String(input.waiting)} still waiting to send.`;

  const pictures = picturesLine(input.photos, input.photoBytes);

  if (input.standalone) {
    return {
      counts,
      where: 'You are in the UpSurv app, opened from the Home Screen icon.',
      pictures,
    };
  }

  return {
    counts,
    where: 'You are in a browser window, not the app on your Home Screen.',
    // containerNote retired with D-029 R1 — the where-line is the diagnosis;
    // a backup file is the answer either way.
    pictures,
  };
}

/* ═══════════════════════ the survey list's standing notice ═══════════════ */

/*
 * `homeStorageNotice` — the standing only-copy warning on Home — lived here
 * from D-020 to 2026-08-31. D-029 R1 removed the sentence: the pill carries
 * the linked state, the setup card carries the way out, and the device screen
 * still says everything when asked.
 */
